import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";

const ME_KEY = ["auth", "workspace-me"] as const;

type InviteInfo = {
  ok: true;
  email: string;
  role: string;
  roleLabel: string;
  workspaceName: string | null;
};

export default function InvitePage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Validate the token up front. On a bad/expired/used token this returns a
  // non-2xx and we render the appropriate dead-end state instead of the form.
  const info = useQuery<InviteInfo>({
    queryKey: ["invite", token],
    queryFn: () => api.get<InviteInfo>(`/invite/${token}`),
    enabled: token.length > 0,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: (body: { name: string; password: string }) =>
      api.post<{ ok: true }>(`/invite/${token}`, body),
    onSuccess: async () => {
      // Cookie is set by the accept endpoint — refresh the session query so the
      // route guards see the new editor, then land them in the app.
      await qc.invalidateQueries({ queryKey: ME_KEY });
      navigate("/app", { replace: true });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (accept.isPending) return;
    if (name.trim().length < 1) { setFormError("Please enter your name."); return; }
    if (password.length < 8) { setFormError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setFormError("Passwords don't match."); return; }
    accept.mutate({ name: name.trim(), password });
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (info.isLoading) {
    return (
      <AuthShell>
        <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-8 text-center shadow-card backdrop-blur">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">Checking your invite…</p>
        </div>
      </AuthShell>
    );
  }

  // ── Invalid / expired / already used ─────────────────────────────────────────
  if (info.isError) {
    const status = info.error instanceof ApiError ? info.error.status : 0;
    const message =
      status === 410
        ? "This invite link has expired or has already been used."
        : "This invite link isn't valid. Ask your workspace admin to send a new one.";
    return (
      <AuthShell>
        <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-6 text-center shadow-card backdrop-blur md:p-8">
          <h1 className="font-display text-2xl font-black tracking-tight">Invite unavailable</h1>
          <p className="mt-2 text-sm text-muted-foreground">{message}</p>
          <Button asChild variant="outline" className="mt-6">
            <Link to="/login">Go to sign in</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  const workspace = info.data?.workspaceName || "this workspace";

  // ── Accept form ──────────────────────────────────────────────────────────────
  return (
    <AuthShell>
      <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-6 shadow-card backdrop-blur md:p-8">
        <h1 className="font-display text-3xl font-black tracking-tight">
          Join {workspace}
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          You've been invited as{" "}
          <span className="font-medium text-foreground">{info.data?.roleLabel}</span>. Set a
          password to claim your seat.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input id="invite-email" type="email" value={info.data?.email ?? ""} disabled readOnly />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-name">Your name</Label>
            <Input
              id="invite-name"
              type="text"
              autoComplete="name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={accept.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-password">Password</Label>
            <div className="relative">
              <Input
                id="invite-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={accept.isPending}
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 grid w-9 place-items-center text-muted-foreground transition-colors hover:text-foreground"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Must be at least 8 characters.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-confirm">Confirm password</Label>
            <Input
              id="invite-confirm"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={accept.isPending}
            />
          </div>

          {(formError || accept.isError) && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {formError ||
                (accept.error instanceof ApiError
                  ? accept.error.message
                  : "Couldn't complete signup. Try again.")}
            </div>
          )}

          <Button type="submit" size="lg" className="group w-full" disabled={accept.isPending}>
            {accept.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Setting up…
              </>
            ) : (
              <>
                Join workspace
                <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </form>
      </div>

      <div className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-foreground transition-colors hover:text-brand">
          Sign in
        </Link>
      </div>
    </AuthShell>
  );
}
